import React, { useState, useEffect, useRef } from "react";
import { roleUtilisateur } from "../services/roles";
import api, { clearStoredTokens } from "../services/api";
import { openPublicLegalPage } from "../platform";

export default function ProfileModal({ user, onClose, onSave, consumptionOnly = false, onAccountDeleted }) {
  const [nomComplet,    setNomComplet]    = useState(user?.nom_complet    || "");
  const [email,         setEmail]         = useState(user?.email          || "");
  const [ecole,         setEcole]         = useState(user?.ecole          || "");
  const [classeId,      setClasseId]      = useState(user?.classe_id      || "");
  const [classes,       setClasses]       = useState([]);
  const [telephone,     setTelephone]     = useState(user?.telephone      || "");
  const [dateNaissance, setDateNaissance] = useState(user?.date_naissance || "");
  const [photoFile,     setPhotoFile]     = useState(null);
  const [photoPreview,  setPhotoPreview]  = useState(user?.photo         || null);

  const [showPassword,          setShowPassword]          = useState(false);
  const [currentPassword,       setCurrentPassword]       = useState("");
  const [newPassword,           setNewPassword]           = useState("");
  const [confirmPassword,       setConfirmPassword]       = useState("");
  const [showCurrentPwd,        setShowCurrentPwd]        = useState(false);
  const [showNewPwd,            setShowNewPwd]            = useState(false);
  const [showConfirmPwd,        setShowConfirmPwd]        = useState(false);
  const [showPasswordSuggests,  setShowPasswordSuggests]  = useState(false);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState(false);
  const [showDeletion, setShowDeletion] = useState(false);
  const [deletionPassword, setDeletionPassword] = useState("");
  const [deletionConfirmation, setDeletionConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef();

  // Matières (enseignants uniquement)
  const isTeacher = user?.is_staff || user?.is_superuser;
  const [allMatieres,  setAllMatieres]  = useState([]);
  const [selectedMats, setSelectedMats] = useState((user?.matieres || []).map(m => m.id));

  useEffect(() => {
    api.get("classes/").then(r => setClasses(r.data?.results || r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isTeacher) {
      api.get("matieres/").then(r => setAllMatieres(r.data.results || r.data)).catch(() => {});
    }
  }, [isTeacher]);

  useEffect(() => {
    setNomComplet(user?.nom_complet    || "");
    setEmail(user?.email               || "");
    setEcole(user?.ecole               || "");
    setClasseId(user?.classe_id        || "");
    setTelephone(user?.telephone       || "");
    setDateNaissance(user?.date_naissance || "");
    setPhotoPreview(user?.photo        || null);
    setPhotoFile(null);
    setSelectedMats((user?.matieres || []).map(m => m.id));
    setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    setError(""); setSuccess(false); setShowPassword(false);
  }, [user]);

  const toggleMat = (id) => setSelectedMats(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function handleDeleteAccount() {
    if (deletionConfirmation !== "SUPPRIMER MON COMPTE") {
      setError("Recopiez exactement SUPPRIMER MON COMPTE pour confirmer.");
      return;
    }
    if (!deletionPassword) {
      setError("Saisissez votre mot de passe actuel.");
      return;
    }
    setDeleting(true);
    setError("");
    try {
      await api.delete("me/account/", { data: { password: deletionPassword, confirmation: deletionConfirmation } });
      clearStoredTokens();
      onAccountDeleted?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "La suppression du compte n’a pas pu être effectuée.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setSuccess(false);

    if (showPassword) {
      if (!currentPassword) { setError("Veuillez entrer votre mot de passe actuel."); return; }
      if (!newPassword)     { setError("Veuillez entrer le nouveau mot de passe."); return; }
      const { score } = pwdStrength(newPassword);
      if (score < 4) {
        setError("Mot de passe insuffisant : 8 caractères minimum, une majuscule, un chiffre et un caractère spécial.");
        setShowPasswordSuggests(true);
        return;
      }
      if (newPassword !== confirmPassword) { setError("Les mots de passe ne correspondent pas."); return; }
    }

    setLoading(true);
    try {
      let r;

      if (photoFile) {
        // FormData uniquement quand il y a une photo à uploader.
        // transformRequest supprime le Content-Type par défaut d'Axios (application/json)
        // pour que le navigateur définisse lui-même le boundary multipart.
        const fd = new FormData();
        fd.append("nom_complet",    nomComplet.trim());
        fd.append("email",          email.trim());
        fd.append("ecole",          ecole.trim());
        if (classeId) fd.append("classe_id", classeId);
        fd.append("telephone",      telephone.trim());
        fd.append("date_naissance", dateNaissance || "");
        fd.append("photo",          photoFile);
        if (isTeacher) fd.append("matiere_ids", JSON.stringify(selectedMats));
        if (showPassword && newPassword) {
          fd.append("current_password", currentPassword);
          fd.append("new_password",     newPassword);
        }
        r = await api.patch("me/", fd, {
          transformRequest: [(data, headers) => {
            delete headers["Content-Type"];
            delete headers["content-type"];
            return data;
          }],
        });
      } else {
        // Pas de photo → JSON simple, aucun problème de boundary
        const payload = {
          nom_complet:    nomComplet.trim(),
          email:          email.trim(),
          ecole:          ecole.trim(),
          classe_id:      classeId || null,
          telephone:      telephone.trim(),
          date_naissance: dateNaissance || null,
        };
        if (isTeacher) payload.matiere_ids = selectedMats;
        if (showPassword && newPassword) {
          payload.current_password = currentPassword;
          payload.new_password     = newPassword;
        }
        r = await api.patch("me/", payload);
      }

      setSuccess(true);
      onSave(r.data);
      // Fermeture immédiate après affichage bref du message de succès
      setTimeout(() => { setSuccess(false); onClose(); }, 1000);
    } catch (err) {
      const msg = err?.response?.data?.detail
        || err?.response?.data?.non_field_errors?.[0]
        || "Erreur lors de la mise à jour.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const initials = (nomComplet || user?.username || "?")
    .split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div
      style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(15,23,42,0.55)", display:"flex", alignItems:"center", justifyContent:"center", padding:"16px" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background:"#fff", borderRadius:"16px", width:"100%", maxWidth:"500px", boxShadow:"0 20px 60px rgba(0,0,0,0.25)", overflow:"hidden", maxHeight:"92vh", display:"flex", flexDirection:"column" }}>

        {/* Header */}
        <div style={{ background:"linear-gradient(135deg,#1e3a5f 0%,#2d5a8e 100%)", padding:"24px 24px 20px", display:"flex", alignItems:"center", gap:"16px", flexShrink:0 }}>
          <div
            onClick={() => fileRef.current?.click()}
            style={{ width:64, height:64, borderRadius:"50%", background:"rgba(255,255,255,0.2)", border:"3px solid rgba(255,255,255,0.5)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", overflow:"hidden", flexShrink:0, position:"relative" }}
            title="Changer la photo"
          >
            {photoPreview
              ? <img src={photoPreview} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
              : <span style={{ color:"#fff", fontWeight:700, fontSize:"1.4rem" }}>{initials}</span>}
            <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", opacity:0, transition:"opacity 0.15s" }} className="pm-photo-ov">
              <i className="bi bi-camera" style={{ color:"#fff", fontSize:"1.1rem" }}></i>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handlePhotoChange} />

          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:"#fff", fontWeight:700, fontSize:"1.05rem", lineHeight:1.3 }}>
              {user?.nom_complet || user?.username}
            </div>
            <div style={{ color:"rgba(255,255,255,0.7)", fontSize:"0.82rem", marginTop:2 }}>
              {roleUtilisateur(user)}
            </div>
          </div>

          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:"8px", color:"#fff", width:32, height:32, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Scrollable form body */}
        <form onSubmit={handleSubmit} style={{ padding:"24px", overflowY:"auto", flex:1 }}>
          {error && (
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:"8px", color:"#dc2626", padding:"10px 14px", fontSize:"0.85rem", marginBottom:"16px", display:"flex", gap:"8px", alignItems:"flex-start" }}>
              <i className="bi bi-exclamation-circle-fill" style={{ marginTop:2 }}></i> {error}
            </div>
          )}
          {success && (
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:"8px", color:"#16a34a", padding:"10px 14px", fontSize:"0.85rem", marginBottom:"16px", display:"flex", gap:"8px", alignItems:"center" }}>
              <i className="bi bi-check-circle-fill"></i> Mises à jour des informations effectuées.
            </div>
          )}

          {/* ── Informations générales ── */}
          <SectionTitle>Informations générales</SectionTitle>
          <div style={{ display:"grid", gap:"12px", marginBottom:"20px" }}>
            <Field label="Nom complet" icon="bi-person">
              <input type="text" value={nomComplet} onChange={e => setNomComplet(e.target.value)} placeholder="Votre nom complet" style={inputStyle} />
            </Field>
            <Field label="Adresse email" icon="bi-envelope">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="exemple@domaine.com" style={inputStyle} autoComplete="email" />
            </Field>
            <Field label="École / Établissement" icon="bi-building">
              <input type="text" value={ecole} onChange={e => setEcole(e.target.value)} placeholder="Nom de votre école" style={inputStyle} />
            </Field>
            <Field label="Classe" icon="bi-mortarboard">
              <select value={classeId} onChange={e => setClasseId(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="">— Sélectionner une classe —</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
              <Field label="Téléphone" icon="bi-telephone">
                <input type="tel" value={telephone} onChange={e => setTelephone(e.target.value)} placeholder="Ex : 6 99 99 99 99" style={inputStyle} />
              </Field>
              <Field label="Date de naissance" icon="bi-calendar3">
                <input type="date" value={dateNaissance} onChange={e => setDateNaissance(e.target.value)} style={inputStyle} />
              </Field>
            </div>
          </div>

          {/* ── Matières enseignées (enseignants uniquement) ── */}
          {isTeacher && (
            <div style={{ borderTop:"1px solid #f1f5f9", paddingTop:"16px", marginBottom:"16px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"8px", fontWeight:600, fontSize:"0.9rem", color:"#1e3a5f", marginBottom:"12px" }}>
                <i className="bi bi-journal-bookmark"></i>
                Matières enseignées
              </div>
              {allMatieres.length === 0 ? (
                <div style={{ color:"#94a3b8", fontSize:"0.83rem" }}>Chargement des matières…</div>
              ) : (
                <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
                  {allMatieres.map(m => {
                    const sel = selectedMats.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleMat(m.id)}
                        style={{
                          padding:"5px 14px",
                          borderRadius:"20px",
                          border:`2px solid ${sel ? "#2563eb" : "#e2e8f0"}`,
                          background: sel ? "#eff6ff" : "#f8fafc",
                          color: sel ? "#1d4ed8" : "#64748b",
                          fontWeight: sel ? 600 : 400,
                          fontSize:"0.82rem",
                          cursor:"pointer",
                          transition:"all 0.15s",
                        }}
                      >
                        {sel && <i className="bi bi-check2 me-1" style={{ fontSize:"0.75rem" }}></i>}
                        {m.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Changer le mot de passe ── */}
          <div style={{ borderTop:"1px solid #f1f5f9", paddingTop:"16px" }}>
            <button
              type="button"
              onClick={() => { setShowPassword(v => !v); setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); setError(""); }}
              style={{ display:"flex", alignItems:"center", gap:"8px", background:"none", border:"none", cursor:"pointer", padding:"6px 0", fontSize:"0.88rem", fontWeight:600, color: showPassword ? "#1e3a5f" : "#64748b" }}
            >
              <i className={`bi ${showPassword ? "bi-chevron-down" : "bi-chevron-right"}`} style={{ fontSize:"0.75rem" }}></i>
              <i className="bi bi-lock"></i>
              Changer le mot de passe
            </button>

            {showPassword && (
              <div style={{ display:"grid", gap:"12px", marginTop:"14px", padding:"16px", background:"#f8fafc", borderRadius:"10px", border:"1px solid #e2e8f0" }}>
                <Field label="Mot de passe actuel" icon="bi-lock">
                  <div style={{ position:"relative" }}>
                    <input
                      type={showCurrentPwd ? "text" : "password"}
                      value={currentPassword}
                      onChange={e => setCurrentPassword(e.target.value)}
                      placeholder="Votre mot de passe actuel"
                      style={{ ...inputStyle, paddingRight:"40px" }}
                      autoComplete="current-password"
                    />
                    <button type="button" onClick={() => setShowCurrentPwd(v => !v)} style={eyeBtn}>
                      <i className={`bi ${showCurrentPwd ? "bi-eye-slash" : "bi-eye"}`}></i>
                    </button>
                  </div>
                </Field>
                <Field label="Nouveau mot de passe" icon="bi-lock-fill">
                  <div>
                    <div style={{ position:"relative" }}>
                      <input
                        type={showNewPwd ? "text" : "password"}
                        value={newPassword}
                        onChange={e => { setNewPassword(e.target.value); setShowPasswordSuggests(false); }}
                        placeholder="8 car. min, majuscule, chiffre, spécial"
                        style={{ ...inputStyle, paddingRight:"40px" }}
                        autoComplete="new-password"
                      />
                      <button type="button" onClick={() => setShowNewPwd(v => !v)} style={eyeBtn}>
                        <i className={`bi ${showNewPwd ? "bi-eye-slash" : "bi-eye"}`}></i>
                      </button>
                    </div>
                    {newPassword.length > 0 && (() => {
                      const { score, label, color, criteria } = pwdStrength(newPassword);
                      return (
                        <div style={{ marginTop: 6 }}>
                          {/* Barres */}
                          <div style={{ display:"flex", gap:3, marginBottom:3 }}>
                            {[1,2,3,4].map(i => (
                              <div key={i} style={{ flex:1, height:4, borderRadius:3, background: i <= score ? color : "#e2e8f0", transition:"background 0.3s" }} />
                            ))}
                          </div>
                          <div style={{ fontSize:"0.73rem", color, fontWeight:600, marginBottom:4 }}>{label}</div>
                          {/* Critères */}
                          <ul style={{ fontSize:"0.72rem", margin:0, paddingLeft:14, color:"#64748b" }}>
                            {[
                              { key:"length",    text:"8 caractères minimum" },
                              { key:"uppercase", text:"Une lettre majuscule" },
                              { key:"number",    text:"Un chiffre" },
                              { key:"special",   text:"Un caractère spécial (@, #, !, …)" },
                            ].map(c => (
                              <li key={c.key} style={{ color: criteria[c.key] ? "#22c55e" : "#94a3b8" }}>
                                {criteria[c.key] ? "✓" : "○"} {c.text}
                              </li>
                            ))}
                          </ul>
                          {/* Bouton suggestions */}
                          {score < 4 && (
                            <button
                              type="button"
                              onClick={() => setShowPasswordSuggests(v => !v)}
                              style={{ marginTop:6, fontSize:"0.73rem", background:"none", border:"1px solid #3b82f6", color:"#3b82f6", borderRadius:6, padding:"3px 10px", cursor:"pointer", width:"100%" }}
                            >
                              <i className="bi bi-magic me-1"></i>
                              {showPasswordSuggests ? "Masquer les suggestions" : "Suggérer des mots de passe mémorables"}
                            </button>
                          )}
                          {/* Panneau suggestions */}
                          {showPasswordSuggests && (() => {
                            const suggs = pwdSuggestions(nomComplet);
                            return (
                              <div style={{ marginTop:6, padding:"8px 10px", borderRadius:8, background:"#f0f9ff", border:"1px solid #bae6fd" }}>
                                <div style={{ fontSize:"0.7rem", color:"#0369a1", fontWeight:600, marginBottom:5 }}>
                                  <i className="bi bi-lightbulb-fill me-1" style={{ color:"#f59e0b" }}></i>
                                  Cliquez pour utiliser :
                                </div>
                                {suggs.map((s, i) => (
                                  <div
                                    key={i}
                                    onClick={() => { setNewPassword(s); setShowPasswordSuggests(false); setShowNewPwd(true); }}
                                    style={{ cursor:"pointer", padding:"4px 8px", marginBottom:3, borderRadius:5, background:"#fff", border:"1px solid #e0f2fe", fontFamily:"monospace", fontSize:"0.82rem", fontWeight:600, color:"#0c4a6e" }}
                                    onMouseEnter={e => e.currentTarget.style.background="#e0f2fe"}
                                    onMouseLeave={e => e.currentTarget.style.background="#fff"}
                                  >
                                    🔑 {s}
                                  </div>
                                ))}
                                <div style={{ fontSize:"0.68rem", color:"#64748b", marginTop:4 }}>
                                  <i className="bi bi-info-circle me-1"></i>Notez votre mot de passe avant de valider.
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                </Field>
                <Field label="Confirmer le nouveau mot de passe" icon="bi-shield-lock">
                  <div style={{ position:"relative" }}>
                    <input
                      type={showConfirmPwd ? "text" : "password"}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Répéter le nouveau mot de passe"
                      style={{ ...inputStyle, paddingRight:"40px", borderColor: confirmPassword && confirmPassword !== newPassword ? "#fca5a5" : undefined }}
                      autoComplete="new-password"
                    />
                    <button type="button" onClick={() => setShowConfirmPwd(v => !v)} style={eyeBtn}>
                      <i className={`bi ${showConfirmPwd ? "bi-eye-slash" : "bi-eye"}`}></i>
                    </button>
                    {confirmPassword && confirmPassword !== newPassword && (
                      <div style={{ fontSize:"0.75rem", color:"#dc2626", marginTop:4 }}>Les mots de passe ne correspondent pas</div>
                    )}
                  </div>
                </Field>
              </div>
            )}
          </div>

          {consumptionOnly && (
            <div style={{ borderTop:"1px solid #e2e8f0", marginTop:20, paddingTop:18 }}>
              <SectionTitle>Confidentialité et compte</SectionTitle>
              <button type="button" onClick={() => openPublicLegalPage("/politique-confidentialite/").catch(() => setError("Impossible d’ouvrir la politique de confidentialité."))} style={{ ...cancelBtnStyle, width:"100%", marginBottom:10 }}>
                <i className="bi bi-shield-check me-2"></i>Politique de confidentialité
              </button>
              {!showDeletion ? (
                <button type="button" onClick={() => { setShowDeletion(true); setError(""); }} style={{ ...cancelBtnStyle, width:"100%", color:"#b91c1c", borderColor:"#fecaca" }}>
                  <i className="bi bi-trash3 me-2"></i>Supprimer mon compte
                </button>
              ) : (
                <div style={{ background:"#fff7f7", border:"1px solid #fecaca", borderRadius:10, padding:14 }} role="region" aria-label="Confirmation de suppression du compte">
                  <p style={{ color:"#991b1b", fontSize:"0.83rem", marginTop:0 }}>Cette action supprime définitivement votre compte, votre profil, vos messages et votre progression. Les écritures financières légalement nécessaires sont conservées sans compte actif associé.</p>
                  <label style={{ display:"block", fontSize:"0.76rem", fontWeight:600, marginBottom:5 }}>Mot de passe actuel</label>
                  <input type="password" value={deletionPassword} onChange={e => setDeletionPassword(e.target.value)} autoComplete="current-password" style={inputStyle} />
                  <label style={{ display:"block", fontSize:"0.76rem", fontWeight:600, margin:"12px 0 5px" }}>Recopiez SUPPRIMER MON COMPTE</label>
                  <input value={deletionConfirmation} onChange={e => setDeletionConfirmation(e.target.value)} style={inputStyle} />
                  <div style={{ display:"flex", gap:8, marginTop:12 }}>
                    <button type="button" disabled={deleting} onClick={handleDeleteAccount} style={{ ...saveBtnStyle, background:"#b91c1c", cursor:deleting?"not-allowed":"pointer" }}>{deleting ? "Suppression…" : "Confirmer la suppression"}</button>
                    <button type="button" disabled={deleting} onClick={() => { setShowDeletion(false); setDeletionPassword(""); setDeletionConfirmation(""); }} style={cancelBtnStyle}>Annuler</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display:"flex", gap:"10px", marginTop:"24px", justifyContent:"flex-end" }}>
            <button type="button" onClick={onClose} style={cancelBtnStyle}>Annuler</button>
            <button type="submit" disabled={loading} style={{ ...saveBtnStyle, background: loading ? "#94a3b8" : "linear-gradient(135deg,#1e3a5f,#2d5a8e)", cursor: loading ? "not-allowed" : "pointer" }}>
              {loading && <span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,0.4)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"pm-spin 0.7s linear infinite" }}></span>}
              {loading ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes pm-spin { to { transform: rotate(360deg); } }
        .pm-photo-ov { opacity: 0 !important; }
        div:hover > .pm-photo-ov { opacity: 1 !important; }
      `}</style>
    </div>
  );
}

function pwdStrength(pwd) {
  const criteria = {
    length:    pwd.length >= 8,
    uppercase: /[A-Z]/.test(pwd),
    number:    /[0-9]/.test(pwd),
    special:   /[^A-Za-z0-9]/.test(pwd),
  };
  const score = Object.values(criteria).filter(Boolean).length;
  if (pwd.length === 0) return { score:0, label:"", color:"#e2e8f0", criteria };
  if (score <= 1)       return { score:1, label:"Faible",  color:"#ef4444", criteria };
  if (score === 2)      return { score:2, label:"Moyen",   color:"#f59e0b", criteria };
  if (score === 3)      return { score:3, label:"Bon",     color:"#3b82f6", criteria };
  return               { score:4, label:"Fort",    color:"#22c55e", criteria };
}

function pwdSuggestions(nom) {
  const parts   = (nom || "Eleve Quick").trim().split(/\s+/);
  const first   = parts[0] || "Eleve";
  const last    = parts[1] || "Quick";
  const cap     = s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  const rev     = s => s.split("").reverse().join("");
  const initials = parts.map(p => p[0].toUpperCase()).join("");
  const yr      = new Date().getFullYear();
  const rand3   = () => Math.floor(100 + Math.random() * 900);
  return [
    `${cap(first)}@${yr}${last[0].toUpperCase()}`,
    `${initials}#Apprendre${rand3()}`,
    `${cap(rev(first))}!${rand3()}`,
    `${cap(first.slice(0,3))}${cap(last.slice(0,3))}$${String(yr).slice(-2)}`,
  ];
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"12px" }}>
      {children}
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <label style={{ display:"block", fontSize:"0.75rem", fontWeight:600, color:"#64748b", marginBottom:"5px" }}>
        <i className={`bi ${icon}`} style={{ marginRight:5 }}></i>{label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width:"100%", padding:"9px 12px", borderRadius:"8px",
  border:"1px solid #e2e8f0", fontSize:"0.88rem", color:"#1e293b",
  outline:"none", background:"#fff", boxSizing:"border-box",
};

const eyeBtn = {
  position:"absolute", right:10, top:"50%", transform:"translateY(-50%)",
  background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:"2px",
};

const cancelBtnStyle = {
  padding:"9px 20px", borderRadius:"8px", border:"1px solid #e2e8f0",
  background:"#fff", color:"#475569", fontWeight:500, cursor:"pointer", fontSize:"0.88rem",
};

const saveBtnStyle = {
  padding:"9px 24px", borderRadius:"8px", border:"none",
  color:"#fff", fontWeight:600, fontSize:"0.88rem",
  display:"flex", alignItems:"center", gap:"8px",
};
